import React from "react";

/**
 * LeftPanelSourceBadge — tiny provenance pill rendered on every item
 * card in the Pricing screen's Inspection Findings left rail.
 *
 * Tells the FOH at a glance where a row came from:
 *   • **MOT**  — DVSA defect, any severity (purple)
 *   • **VI**   — Standard Vehicle Inspection item        (slate)
 *   • **DIAG** — Diagnostic VI confirmed finding         (teal)
 *   • **SVC**  — Service Recommendation / interval       (indigo)
 *
 * Stays out of the way visually — 2.5–3 chars, 9px font, 4px x-padding.
 * Tooltip surfaces the long-form label so a hovering FOH can confirm.
 */
const PALETTE = {
  MOT:  { bg: "bg-purple-600", text: "text-white",  label: "MOT defect" },
  VI:   { bg: "bg-slate-600",  text: "text-white",  label: "Vehicle Inspection" },
  DIAG: { bg: "bg-teal-600",   text: "text-white",  label: "Diagnostic VI" },
  SVC:  { bg: "bg-indigo-600", text: "text-white",  label: "Service Recommendation" },
};

export default function LeftPanelSourceBadge({ source, dataTestid }) {
  const cfg = PALETTE[source];
  if (!cfg) return null;
  return (
    <span
      title={`Source: ${cfg.label}`}
      className={`text-[9px] font-bold tracking-tight px-1 py-0.5 rounded shrink-0 ${cfg.bg} ${cfg.text}`}
      data-testid={dataTestid || `left-panel-source-${source.toLowerCase()}`}
    >
      {source}
    </span>
  );
}

/**
 * Decide which provenance bucket a given VI inspection_item belongs to.
 * Falls back to "VI" — the most common case.
 *
 * Heuristic mirrors the existing diag-priority sort logic in the panel:
 * warning-light / dash-warning items are produced by the Diagnostic VI
 * flow (captured as "dash warning lamps" by the diag tech), everything
 * else is from the standard Vehicle Inspection.
 */
export function classifyInspectionItemSource(item) {
  const key = (item?.item_key || "").toLowerCase();
  if (
    key.includes("dash_warning") ||
    key.includes("warning_lamp") ||
    key.includes("warning_light") ||
    key.includes("diagnostic")
  ) {
    return "DIAG";
  }
  return "VI";
}
